import { useAppStore } from "@/global/store";
import {
  CartEvent,
  cartManagerState,
  selectCartEvents,
  selectCartManager,
} from "@/global/store/data/purchaseSeats/cartManager";
import { createState } from "@/global/store/zustand_store";
import { useDispatch } from "react-redux";
import { PerkItemType, PerkParentType } from "./perks.types";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { toast } from "react-toastify";
import { parseApiErrorMessage } from "@/global/utils/utils";
import { useMutation } from "@tanstack/react-query";
import { getPerksApi } from "@/global/api/perks";
import { useCartHelpers } from "../useCartHelpers";
import { purchaseSeatsTabUIDs } from "@/global/config/constants";
import { findAllPerksLocations, findMandatoryPerks } from "../utils.perks";

/*
+------------------------------------------------------------------------------+
* [ perks provider internal state ]
+------------------------------------------------------------------------------+ */
export const usePerksState = createState({
  isLoadingBaseData: true,
  hasLoadError: false,
  perkParents: [] as PerkParentType[],
});

/*
+------------------------------------------------------------------------------+
* [ perks manager hook ]
+------------------------------------------------------------------------------+ */
export const usePerksManager = () => {
  // app system
  const teamConfig = useTeamConfig();
  const dispatch = useDispatch();

  // apis
  const getPerksReq = useMutation(getPerksApi);

  // business states and helpers
  const cartManager = useAppStore(selectCartManager);
  const perksState = usePerksState();
  const perkParents = perksState.perkParents;
  const cartEvents = useAppStore(selectCartEvents);
  const { callCancelCartApi, switchPurchaseSeatTab } = useCartHelpers();

  const onSelectPerk = (args: {
    perkItem: PerkItemType;
    manualSeatCount?: number | null;
  }) => {
    // handling manual seat count variant
    let { perkItem, manualSeatCount } = args;
    if (
      ["manual", "event-manual", "suite-additional-manual"].includes(
        perkItem?.seatCountVariation
      ) &&
      manualSeatCount
    ) {
      perkItem = { ...perkItem, seatCount: manualSeatCount };
    }

    // dispatching to redux based on perk type
    if (perkItem.perkType == "order-level") {
      dispatch(
        cartManagerState.actions.toggleOLPerkItemSelection({
          perkItem,
        })
      );
    } else {
      dispatch(
        cartManagerState.actions.togglePerkItemSelection({
          perkItem,
        })
      );
    }
  };

  const onClickAddAll = (perkItems: PerkItemType[]) => {
    dispatch(
      cartManagerState.actions.addPerkItemMulti({
        perkItems,
      })
    );
  };

  const onClickRemoveAll = (perkItems: PerkItemType[]) => {
    dispatch(
      cartManagerState.actions.removePerkItemMulti({
        perkItems,
      })
    );
  };

  const onDeleteAllFailedPerks = () => {
    dispatch(cartManagerState.actions.deleteAllFailedPerks());
  };

  const addInitialMandatoryPerksToCart = (perkGroups: PerkParentType[]) => {
    const mandatoryPerks = findMandatoryPerks(perkGroups);
    mandatoryPerks.forEach((perk) => {
      dispatch(
        cartManagerState.actions.addPerkItemSelection({
          perkItem: perk,
        })
      );
    });
  };

  const onLoadPerksBase = async () => {
    perksState.set({ isLoadingBaseData: true, hasLoadError: false });

    const perksLocationsHeld = findAllPerksLocations(cartManager);
    if (perksLocationsHeld.length) {
      await callCancelCartApi({ perkLocations: perksLocationsHeld });
    }

    try {
      if (cartManager?.cart?.length > 0) {
        const perksRes = await getPerksReq.mutateAsync({
          teamSchema: teamConfig.teamSchema,
          cart: cartManager?.cart,
        });
        if (perksRes.data.length == 0) {
          await switchPurchaseSeatTab(purchaseSeatsTabUIDs.checkout);
        } else {
          perksState.set({ perkParents: perksRes.data });
          addInitialMandatoryPerksToCart(perksRes.data);
        }
      }
    } catch (err) {
      perksState.set({ hasLoadError: true });
      toast.error(parseApiErrorMessage(err));
    } finally {
      perksState.set({ isLoadingBaseData: false });
    }
  };

  return {
    getPerksReq,
    perksState,
    cartEvents,
    perkParents,
    onSelectPerk,
    onClickAddAll,
    onClickRemoveAll,
    onDeleteAllFailedPerks,
    onLoadPerksBase,
    hideAddAllButton: teamConfig.uiConfigs.perks?.hideAddAllButton || false,
    merchandisingImages: teamConfig.uiConfigs.perks?.merchandisingImages || [],
  };
};

// fake perks for testing
// ----------------------------------------------
// const clonedPerk1: PerkItemType = {
//   ...perksRes.data[0].perks[0],
//   perkCode: "foo",
//   perkType: "order-level",
//   gameEventCode: "order-level",
//   id: 798,
//   isMandatory: true,
// };
// const clonedPerk3: PerkItemType = {
//   ...perksRes.data[0].perks[0],
//   id: 799,
//   perkCode: "bar",
//   isMandatory: true,
//   seatCountVariation: "manual",
//   perkType: "suite-level",
//   seatCount: 1,
//   increment: 1,
//   minCount: 1,
//   maxCount: 4,
// };
// const clonedPerk2: PerkItemType = {
//   ...perksRes.data[0].perks[0],
//   perkCode: "bax",
//   id: 800,
//   isMandatory: true,
//   perkType: "event-level",
// };

// perksRes.data[0].perks.push(clonedPerk1, clonedPerk2, clonedPerk3);
